import type { AnalysisConfig, MetricKey, YearMetrics } from '../core/domain/types';
import { MONTHS_SHORT, hourLabel } from './format';

export type Tone = 'neutral' | 'warm' | 'cool';

export interface MetricMeta {
  key: MetricKey;
  label: string;
  short: string;
  unit: string;
  decimals: number;
  refDecimals: number;
  tone: Tone;
  kind: 'sum' | 'count' | 'mean';
  /** +1: höherer Wert = wärmeres Jahr (rot); −1: höherer Wert = kühleres Jahr (blau). */
  warmSense: 1 | -1;
  definition: (c: AnalysisConfig) => string;
  /** Anteil vollständiger Beobachtungen für Teiljahre (Nenner). */
  coverage: (m: YearMetrics) => { observed: number; expected: number; unit: string };
}

const days = (m: YearMetrics) => ({ observed: m.daysValid, expected: m.daysExpected, unit: 'Tage' });
const nights = (m: YearMetrics) => ({ observed: m.nightsValid, expected: m.nightsExpected, unit: 'Nächte' });
const seasonNights = (m: YearMetrics) => ({ observed: m.seasonNightsValid, expected: m.seasonNightsExpected, unit: 'Nächte' });

export const nightWindow = (c: AnalysisConfig) => `${hourLabel(c.nightStartHour)}–${hourLabel(c.nightEndHour)}`;
export const season = (c: AnalysisConfig) => `${MONTHS_SHORT[c.coolingSeasonStartMonth - 1]}–${MONTHS_SHORT[c.coolingSeasonEndMonth - 1]}`;

export const METRICS: Record<MetricKey, MetricMeta> = {
  hdd: {
    key: 'hdd',
    label: 'Heizgradtage',
    short: 'HGT',
    unit: 'K·d',
    decimals: 0,
    refDecimals: 0,
    tone: 'neutral',
    kind: 'sum',
    warmSense: -1,
    definition: (c) => `Basis ${c.hddBase} °C`,
    coverage: days,
  },
  cdd: {
    key: 'cdd',
    label: 'Kühlgradtage',
    short: 'KGT',
    unit: 'K·d',
    decimals: 1,
    refDecimals: 1,
    tone: 'warm',
    kind: 'sum',
    warmSense: 1,
    definition: (c) => `Basis ${c.cddBase} °C`,
    coverage: days,
  },
  warmNights: {
    key: 'warmNights',
    label: 'Warme Nächte',
    short: 'Warme Nächte',
    unit: 'Nächte',
    decimals: 0,
    refDecimals: 1,
    tone: 'warm',
    kind: 'count',
    warmSense: 1,
    definition: (c) => `Minimum ≥ ${c.warmNightThreshold} °C, ${nightWindow(c)}`,
    coverage: nights,
  },
  coolHours: {
    key: 'coolHours',
    label: 'Geeignete Kühlstunden',
    short: 'Kühlstunden',
    unit: 'h',
    decimals: 0,
    refDecimals: 0,
    tone: 'cool',
    kind: 'sum',
    warmSense: -1,
    definition: (c) => `Nachts ≤ ${c.coolingRef - c.coolingMinDelta} °C (${c.coolingRef} °C − ${c.coolingMinDelta} K), ${season(c)}`,
    coverage: seasonNights,
  },
  coolKh: {
    key: 'coolKh',
    label: 'Theoretisches Kühlpotenzial',
    short: 'Kühlpotenzial',
    unit: 'K·h',
    decimals: 0,
    refDecimals: 0,
    tone: 'cool',
    kind: 'sum',
    warmSense: -1,
    definition: (c) => `Σ (${c.coolingRef} °C − T) in geeigneten Nachtstunden, ${season(c)}`,
    coverage: seasonNights,
  },
  hotDays: {
    key: 'hotDays',
    label: 'Heisse Tage',
    short: 'Heisse Tage',
    unit: 'Tage',
    decimals: 0,
    refDecimals: 1,
    tone: 'warm',
    kind: 'count',
    warmSense: 1,
    definition: (c) => `Stundenmaximum ≥ ${c.hotDayThreshold} °C`,
    coverage: days,
  },
  heatEvents: {
    key: 'heatEvents',
    label: 'Hitzeperioden',
    short: 'Hitzeperioden',
    unit: 'Perioden',
    decimals: 0,
    refDecimals: 1,
    tone: 'warm',
    kind: 'count',
    warmSense: 1,
    definition: (c) => `≥ ${c.heatPeriodMinDays} Tage in Folge ≥ ${c.hotDayThreshold} °C, gewählte Definition`,
    coverage: days,
  },
  heatDays: {
    key: 'heatDays',
    label: 'Tage in Hitzeperioden',
    short: 'Hitzeperiodentage',
    unit: 'Tage',
    decimals: 0,
    refDecimals: 1,
    tone: 'warm',
    kind: 'count',
    warmSense: 1,
    definition: (c) => `Tage in Perioden ≥ ${c.heatPeriodMinDays} Tage`,
    coverage: days,
  },
  heatLongest: {
    key: 'heatLongest',
    label: 'Längste Hitzeperiode',
    short: 'Längste Periode',
    unit: 'Tage',
    decimals: 0,
    refDecimals: 1,
    tone: 'warm',
    kind: 'count',
    warmSense: 1,
    definition: () => 'Dauer der längsten im Jahr beginnenden Periode',
    coverage: days,
  },
  tMean: {
    key: 'tMean',
    label: 'Jahresmitteltemperatur',
    short: 'Jahresmittel',
    unit: '°C',
    decimals: 1,
    refDecimals: 1,
    tone: 'neutral',
    kind: 'mean',
    warmSense: 1,
    definition: () => 'Mittel der gültigen Tagesmittel',
    coverage: days,
  },
  tropicalNights: {
    key: 'tropicalNights',
    label: 'Tropennächte',
    short: 'Tropennächte',
    unit: 'Nächte',
    decimals: 0,
    refDecimals: 1,
    tone: 'warm',
    kind: 'count',
    warmSense: 1,
    definition: () => 'Tagesminimum ≥ 20 °C (Standarddefinition)',
    coverage: days,
  },
  veryHotDays: {
    key: 'veryHotDays',
    label: 'Sehr heisse Tage',
    short: 'Sehr heisse Tage',
    unit: 'Tage',
    decimals: 0,
    refDecimals: 1,
    tone: 'warm',
    kind: 'count',
    warmSense: 1,
    definition: () => 'Stundenmaximum ≥ 35 °C (Standardschwelle)',
    coverage: days,
  },
  txx: {
    key: 'txx',
    label: 'Höchstwert des Jahres',
    short: 'TXx',
    unit: '°C',
    decimals: 1,
    refDecimals: 1,
    tone: 'warm',
    kind: 'mean',
    warmSense: 1,
    definition: () => 'Höchster Stundenwert des Kalenderjahres (TXx)',
    coverage: days,
  },
};

export const METRICS_EXTRA_NOTE = 'Tropennächte, sehr heisse Tage und TXx verwenden feste Standardschwellen, damit sie mit Klimaszenarien vergleichbar sind.';

export const MAIN_METRICS: MetricKey[] = ['hdd', 'cdd', 'warmNights', 'coolHours'];
export const ALL_METRICS: MetricKey[] = ['hdd', 'cdd', 'warmNights', 'coolHours', 'coolKh', 'hotDays', 'heatEvents', 'heatDays', 'heatLongest', 'tMean', 'tropicalNights', 'veryHotDays', 'txx'];

export function toneColor(t: Tone): string {
  return t === 'warm' ? 'var(--warm)' : t === 'cool' ? 'var(--cool)' : 'var(--primary)';
}

export function toneHex(t: Tone): string {
  return t === 'warm' ? '#c8202a' : t === 'cool' ? '#1c63a6' : '#1e3a40';
}

export function metricValue(m: YearMetrics, k: MetricKey): number | null {
  const v = m[k];
  return typeof v === 'number' ? v : null;
}
